<!-- src/components/QuestionModal.vue -->
<template>
  <teleport to="body">
    <transition name="fade">
      <div
        v-if="open"
        class="fixed inset-0 z-50 flex items-center justify-center font-berthold-force"
        @keydown.esc.prevent="handleEsc"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/60 z-20"
          @click="close()"
        ></div>

        <!-- Wrapper para respeitar o spacing do flowbite --> 
        <div class="relative p-4 w-full max-w-md max-h-full z-20">
          <!-- Dialog -->
          <div
            ref="panelRef"
            class="relative rounded-lg shadow-sm bg-black focus:outline-none border"
            :class="[
              status == 'success' ? 'border-[#26a260]' : '',
              status == 'failed' ? 'border-[#d20a0a]' : '',
            ]"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="titleId"
          >
            <!-- Header -->
            <div
              class="grid grid-cols-[auto_2rem] justify-stretch uppercase py-2 px-2 border-b rounded-t-lg border-[#222222]"
            >
              <h3
                :id="titleId"
                class="text-2xl font-semibold tracking-wider text-white my-auto mx-auto pl-14"
              >
                {{ 
                  status === 'failed' 
                  ? 'NOTA NÃO ALCANÇADA 😞'
                  : 'PARABÉNS 🎉' ?? ''
                }}
              </h3>

              <!-- botão de fechar | desativado -->
              <button
                type="button"
                class="text-gray-400 bg-transparent rounded-lg text-sm h-8 w-8 ms-auto inline-flex justify-center items-center hover:bg-gray-600 hover:text-white z-20"
                @click="close()"
              >
                <svg
                  class="w-5 h-5"
                  viewBox="0 0 14 14"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                  />
                </svg>
                <span class="sr-only">Fechar</span>
              </button>
            </div>

            <!-- Body -->
            <div class="h-[40vh] flex flex-col items-center justify-center text-center p-4 z-10">
              
              <!-- FAILED -->
              <template v-if="status === 'failed'">
                <lottie
                  :options="animationOptions"
                  @animCreated="handleAnimation"
                  class="w-full max-w-[460px] transition-transform duration-500 ease-out z-10"
                  :class="animationCompleted ? 'scale-[2.3] z-10' : 'scale-100 z-10'"
                  height="260"
                />

                <h2 class="tracking-wider text-white text-3xl">
                  Pontuação necessária não alcançada <br />
                </h2>
              </template>

              <!-- SUCCESS -->
              <template v-else-if="status === 'success'">
                <lottie
                  :options="successAnimate"
                  @animCreated="handleAnimation"
                  class="w-full transition-transform duration-500 ease-out"
                  :class="animationCompleted ? 'scale-[1.8]' : 'scale-100'"
                  height="420"
                />

                <h2 class="tracking-wider text-white text-3xl">
                  Você concluiu todas as aulas
                </h2>
              </template>

            </div>

            <!-- Rodapé -->
            <div v-if="status == 'failed'" class="flex items-center justify-between tracking-wider p-2">
              <span class="text-sm text-gray-400">
                REFAÇA ESTA AULA
              </span>

              <button
                type="button"
                class="inline-flex items-center justify-center px-5 py-2.5 text-base focus:outline-none uppercase text-white font-bold tracking-wide bg-[#d20a0a] border-2 border-red-700 hover:bg-white hover:text-red-700 transition-colors duration-300 disabled:hover:text-gray-300 disabled:text-gray-400 disabled:bg-[#222222] disabled:border-2 disabled:border-gray-700 disabled:opacity-60 disabled:cursor-not-allowed z-20"
                title="Realizar nova tentativa"
                @click="restart()"
              >
                tentar novamente
                <svg
                  class="w-4 h-4 ms-1.5 -me-0.5"
                  viewBox="0 0 14 10"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M1 5h12m0 0L9 1m4 4L9 9"
                  />
                </svg>
              </button>
            </div>

            <!-- success -->
            <div v-if="status == 'success'" class="flex justify-items-center tracking-wider mb-2">
              <span class="text-lg text-gray-400 mx-auto">
                Realize a avaliação para emitir seu certificado
              </span>
            </div>

          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { dataShared } from "@/shared/store";
import { dataStatus } from "@/shared/store/status";
import { computed, onMounted, onBeforeUnmount, ref, watch } from "vue";

// lotties
const animationOptions = ref({
  animationData: require('@/assets/lotties/FailedLottieAnimation.json'),
  loop: false,
  autoplay: true
})
const successAnimate = ref({
  animationData: require('@/assets/lotties/SuccessLottieAnimation.json'),
  loop: false,
  autoplay: true
})
const animationCompleted = ref(false)
const handleAnimation = (anim) => {
  anim.addEventListener('complete', () => {
    animationCompleted.value = true
  })
}

// modal
const open = computed(() => dataStatus.getters.modalOpen)
const status = computed(() => dataStatus.getters.status) // resultado da aula

function close () {
  dataStatus.dispatch('setModalOpen', false)
}

watch(open.value, (val) => {
  if (!val) animationCompleted.value = false
})

function restart() {
  dataShared.dispatch("restartLesson");
  close()
}

function handleEsc() {
  if (open) close();
}

const titleId = `question-title-${Math.random().toString(36).slice(2, 8)}`;
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
